import { useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, Users, Calendar, Crown, ArrowRight, Sparkles, BookOpen,
  CheckCircle2, Clock, Shield, Phone,
} from "lucide-react";
import { joinGroupOrContactLink, directChatLink, hasGroupInvite } from "@/lib/whatsapp";

const MSG_GRUPO = `Hola 👋 Soy abogado/a o mediador/a y quiero unirme al grupo de Colega Link. Por favor envíame el enlace de invitación al grupo de WhatsApp. ¡Gracias!`;

const MSG_CLASE = `Hola 👋 Quiero reservar mi cupo para la clase informativa de Colega Link.

Mis datos:
• Nombre:
• RUT:
• Soy: Abogado/a / Mediador/a
• Región / Comuna:

¿Cuándo es la próxima clase? Quiero participar.`;

const MSG_PIONERO_RESERVA = `Hola 👋 Quiero reservar mi cupo Pionero de Colega Link ($15.000 CLP · 50 cupos · se abona al plan anual Fundador).

Mis datos:
• Nombre:
• RUT:
• Email:
• Soy: Abogado/a / Mediador/a
• Región / Comuna:

Por favor envíenme los datos para hacer la transferencia y confirmar mi cupo.`;

const BENEFITS = [
  { icon: Users, title: "Comunidad de colegas", desc: "Grupo exclusivo en WhatsApp con abogados y mediadores verificados de toda Chile." },
  { icon: BookOpen, title: "Clase informativa gratis", desc: "Te mostramos en vivo cómo funciona la plataforma, el Pago Protegido, OJV y delegaciones." },
  { icon: Crown, title: "Cupo Pionero asegurado", desc: "Reserva uno de los 50 cupos vitalicios con $15.000 CLP (se abona al plan anual Pionero Fundador)." },
];

const STEPS = [
  { n: 1, title: "Únete al grupo WhatsApp", desc: "Recibe el link de invitación al grupo Colega Link." },
  { n: 2, title: "Asiste a la clase informativa", desc: "Te explicamos en vivo el funcionamiento. Resuelves dudas con el equipo." },
  { n: 3, title: "Reserva tu cupo Pionero", desc: "$15.000 CLP (no reembolsables) — se abonan íntegros a tu plan anual Pionero Fundador si completas el reto (15 referidos validados)." },
  { n: 4, title: "Verificamos credenciales", desc: "24h hábiles. Te avisamos cuando estés activo en la plataforma." },
];

export default function Comunidad() {
  const [activeCta, setActiveCta] = useState(null);
  const groupConfigured = hasGroupInvite();

  const openGroup = (key) => {
    setActiveCta(key);
    window.open(joinGroupOrContactLink(MSG_GRUPO), "_blank", "noopener,noreferrer");
    setTimeout(() => setActiveCta(null), 2500);
  };

  const openDirect = (key, msg) => {
    setActiveCta(key);
    window.open(directChatLink(msg), "_blank", "noopener,noreferrer");
    setTimeout(() => setActiveCta(null), 2500);
  };

  return (
    <div className="container-app py-12 max-w-6xl" data-testid="comunidad-page">
      {/* Hero */}
      <section className="text-center">
        <div className="label-eyebrow inline-flex items-center gap-2">
          <Sparkles size={12} /> Lanzamiento MVP · Comunidad
        </div>
        <h1 className="serif text-5xl md:text-6xl mt-4 leading-[0.95] tracking-tight">
          Sé parte de la <span className="brand-gradient-text">comunidad</span> que está cambiando
          <br className="hidden md:block" />
          la forma de delegar en Chile.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          WhatsApp no es una red profesional. Estamos construyendo la primera red verificada de abogados y mediadores
          de Chile, con Pago Protegido e integración real con la OJV.
        </p>
      </section>

      {/* 3 main CTAs */}
      <section className="mt-16 grid md:grid-cols-3 gap-4">
        <CTACard
          icon={MessageCircle}
          color="emerald"
          title={groupConfigured ? "Unirme al grupo WhatsApp" : "Solicitar acceso al grupo"}
          subtitle={groupConfigured ? "Comunidad cerrada · entras al toque" : "Comunidad cerrada"}
          desc={groupConfigured
            ? "Únete directo al grupo Colega Link en WhatsApp. Conoce a otros colegas, comparte dudas y ve novedades antes que nadie."
            : "Te enviamos el link de invitación al grupo Colega Link por WhatsApp. Conoce a otros colegas, comparte dudas y ve novedades antes que nadie."}
          badge="Gratis · sin compromiso"
          cta={groupConfigured ? "Unirme al grupo ahora" : "Pedir link del grupo"}
          loading={activeCta === "grupo"}
          onClick={() => openGroup("grupo")}
          testid="cta-grupo"
        />
        <CTACard
          icon={BookOpen}
          color="cyan"
          title="Reservar clase informativa"
          subtitle="En vivo · cupo limitado"
          desc="Te mostramos en vivo cómo funciona la plataforma, el flujo OJV, el Pago Protegido y los beneficios de la membresía Pionero. Resolvemos todas tus dudas."
          badge="Gratis · 30 min"
          cta="Reservar mi cupo"
          loading={activeCta === "clase"}
          onClick={() => openDirect("clase", MSG_CLASE)}
          testid="cta-clase"
        />
        <CTACard
          icon={Crown}
          color="amber"
          title="Reservar membresía Pionero"
          subtitle="50 cupos vitalicios"
          desc="Asegura tu cupo Pionero con $15.000 CLP (no reembolsables · se abonan al plan anual Pionero Fundador). Badge, 0% de comisión en las primeras 10 delegaciones y Colega IA incluida."
          badge="$15.000 CLP"
          cta="Reservar mi cupo Pionero"
          loading={activeCta === "pionero"}
          onClick={() => openDirect("pionero", MSG_PIONERO_RESERVA)}
          testid="cta-pionero"
          extraAction={
            <Link to="/embajador-pionero" className="text-xs text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 mt-2" data-testid="cta-pionero-landing">
              Ver detalles del programa <ArrowRight size={11}/>
            </Link>
          }
        />
      </section>

      {/* Trust strip */}
      <section className="mt-16 panel p-6">
        <div className="grid sm:grid-cols-3 gap-6 text-sm">
          <Trust icon={Shield} title="Verificación de credenciales" desc="Validamos cédula, título y certificado de la Corte Suprema." />
          <Trust icon={CheckCircle2} title="Pago Protegido Colega Link" desc="El dinero queda retenido hasta que se cumpla la gestión." />
          <Trust icon={Phone} title="Soporte directo" desc="Hablamos por WhatsApp con cada colega. Sin bots ni formularios." />
        </div>
      </section>

      {/* Steps */}
      <section className="mt-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="label-eyebrow">Cómo entrar</div>
          <h2 className="serif text-4xl md:text-5xl mt-3 leading-tight">
            4 pasos. <span className="text-emerald-300">Sin formularios largos.</span>
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s) => (
            <div key={s.n} className="panel p-6 relative" data-testid={`step-${s.n}`}>
              <div className="w-9 h-9 rounded-full bg-emerald-400/15 text-emerald-300 flex items-center justify-center font-bold text-sm ring-1 ring-emerald-400/30">
                {s.n}
              </div>
              <div className="mt-4 font-medium">{s.title}</div>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="mt-20 grid md:grid-cols-3 gap-4">
        {BENEFITS.map((b) => (
          <div key={b.title} className="panel p-6">
            <b.icon size={20} className="text-[hsl(var(--accent))]" />
            <div className="mt-4 serif text-xl">{b.title}</div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
          </div>
        ))}
      </section>

      {/* Final CTA */}
      <section className="mt-20 panel p-10 md:p-14 ring-1 ring-emerald-400/30 bg-gradient-to-br from-emerald-950/20 via-background to-background text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 50% 0%, hsl(150 80% 50% / 0.25), transparent 60%)" }} />
        <div className="relative">
          <MessageCircle size={28} className="text-emerald-400 mx-auto" />
          <h2 className="serif text-4xl md:text-5xl mt-4 leading-tight">¿Listo para sumarte?</h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
            Empieza por el grupo de WhatsApp. Es el paso más rápido y desde ahí coordinamos todo.
          </p>
          <button
            onClick={() => openGroup("grupo-final")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium bg-[#25D366] text-white hover:bg-[#1ebe5d] transition-colors mt-8 disabled:opacity-50"
            data-testid="cta-grupo-final"
            disabled={activeCta === "grupo-final"}
          >
            <MessageCircle size={16} /> {activeCta === "grupo-final" ? "Abriendo WhatsApp…" : (groupConfigured ? "Unirme al grupo Colega Link" : "Pedir link del grupo Colega Link")}
          </button>
          <div className="mt-4 text-xs text-muted-foreground">
            {groupConfigured
              ? "Entras directo al grupo de WhatsApp. Es una comunidad cerrada y moderada."
              : <>Te escribimos al <span className="text-foreground">+56 9 4119 9600</span>. Respondemos en menos de 1 hora hábil.</>}
          </div>
        </div>
      </section>
    </div>
  );
}

function CTACard({ icon: Icon, color, title, subtitle, desc, badge, cta, loading, onClick, testid, extraAction }) {
  const palette = {
    emerald: { ring: "ring-emerald-400/40", glow: "from-emerald-500/20", icon: "text-emerald-300", badge: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30", btn: "bg-[#25D366] hover:bg-[#1ebe5d] text-white" },
    cyan: { ring: "ring-cyan-400/40", glow: "from-cyan-500/20", icon: "text-cyan-300", badge: "bg-cyan-400/15 text-cyan-300 border-cyan-400/30", btn: "bg-cyan-500 hover:bg-cyan-400 text-cyan-950" },
    amber: { ring: "ring-amber-400/40", glow: "from-amber-500/20", icon: "text-amber-300", badge: "bg-amber-400/15 text-amber-300 border-amber-400/30", btn: "bg-amber-500 hover:bg-amber-400 text-amber-950" },
  }[color];

  return (
    <div className={`panel p-7 ring-1 ${palette.ring} relative overflow-hidden flex flex-col`}>
      <div className={`absolute -top-12 -right-12 w-40 h-40 blur-3xl opacity-50 bg-gradient-to-br ${palette.glow} to-transparent rounded-full pointer-events-none`} />
      <div className="relative flex-1">
        <div className="flex items-start justify-between gap-3">
          <Icon size={26} className={palette.icon} />
          <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded-full border ${palette.badge}`}>{badge}</span>
        </div>
        <div className="mt-5">
          <div className="text-[10px] label-eyebrow">{subtitle}</div>
          <h3 className="serif text-2xl mt-1 leading-tight">{title}</h3>
        </div>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{desc}</p>
      </div>
      <div className="relative mt-5">
        <button
          onClick={onClick}
          disabled={loading}
          className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${palette.btn}`}
          data-testid={testid}
        >
          {loading ? <><Clock size={14}/> Abriendo WhatsApp…</> : <><MessageCircle size={14}/> {cta}</>}
        </button>
        {extraAction}
      </div>
    </div>
  );
}

function Trust({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={20} className="text-[hsl(var(--accent))] shrink-0 mt-0.5" />
      <div>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-muted-foreground leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}
